'use client';

import {
  selectJobType,
  selectTrackedLinks,
} from '@/containers/Jobs/redux/jobEditors.selectors';
import { addLinkToInitialMessage } from '@/containers/Jobs/redux/jobEditors.slice';
import { Modals } from '@/containers/Modals/all-modals';
import { addOpenModal } from '@/containers/Modals/redux/modals.slice';
import TrackedLinksList from '@/containers/Modals/ViewTrackedLinksModal/TrackedLinksList';
import { useAppDispatch } from '@/lib/redux/store';
import classNames from 'classnames';
import { HTMLAttributes, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

interface LinksDropdownProps extends HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
  jobId?: string;
}

const LinksDropdown = ({ className, style, onClose, jobId = '' }: LinksDropdownProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const trackedLinks = useSelector(selectTrackedLinks(jobId));
  const jobType = useSelector(selectJobType(jobId));

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (trackedLinks.length === 1 && jobType === 'track-sent-links') {
      // Track-sent-links jobs with exactly one link: auto-insert it.
      dispatch(addLinkToInitialMessage({ jobId, link: trackedLinks[0] }));
    } else {
      // Otherwise always open the Add Tracked Link modal so the user can
      // create a new link. The dropdown's existing-link list isn't reachable
      // visually (the toolbar button suppresses focus to keep editor focus,
      // which prevents daisyUI's focus-within rule from showing it), so
      // funnel every click into the modal instead.
      dispatch(
        addOpenModal({
          modalName: Modals.AddTrackedLinkModal,
          modalVariant: 'narrow',
          modalParams: JSON.stringify({
            jobId,
            allowInsertion: true,
          }),
        })
      );
    }
    setTimeout(() => {
      onClose?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      tabIndex={0}
      className={classNames(
        'card dropdown-content card-compact bg-base-100 z-[1] max-h-60 w-[518px] flex-nowrap overflow-auto rounded-xl border-4 border-[rgba(0,0,0,0.04)] shadow',
        className
      )}
      style={style}
      ref={ref}>
      <div className="card-body !px-2 !py-1">
        <TrackedLinksList
          jobId=""
          links={trackedLinks}
          className="w-full"
          showEmptyContent
          allowInsertion={true}
          size="sm"
          onInsert={onClose}
        />
      </div>
    </div>
  );
};

export default LinksDropdown;
